import { useCallback, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AudioPlayer,
  Button,
  Card,
  EmptyState,
  Icon,
  ProgressBar,
  Skeleton,
  Tag,
  useToast,
} from "@xinghai/ui";
import type { PerceptionTrial } from "@xinghai/shared";
import { studentApi } from "../../lib/api";
import type { PerceptionItem } from "../../lib/api";
import { qk } from "../../lib/queries";
import { getSessionId } from "../../lib/audio/session";
import { Illustration } from "../shared/Illustration";

export type TrialDraft = Omit<PerceptionTrial, "speaker_id" | "id">;

export interface AnswerOption {
  value: string;
  label: string;
}

/** Answer buttons per trial type (docs/UX.md 3). */
export function answerOptions(item: PerceptionItem): AnswerOption[] {
  if (item.trial_type === "AX") {
    return [
      { value: "same", label: "一样" },
      { value: "different", label: "不一样" },
    ];
  }
  if (item.trial_type === "ABX") {
    return [
      { value: "A", label: "更像第一段" },
      { value: "B", label: "更像第二段" },
    ];
  }
  return (item.options ?? []).map((o) => ({ value: o, label: o }));
}

/**
 * Build one behavioural trial row. `correct` stays null when the material
 * carries no key — this is behaviour data, never a grade (ARCHITECTURE.md §4).
 */
export function buildTrial(
  item: PerceptionItem,
  response: string,
  rtMs: number | null,
  sessionId: string,
): TrialDraft {
  return {
    trial_type: item.trial_type,
    stimuli: item.stimuli,
    response,
    correct: item.answer == null ? null : response === item.answer,
    rt_ms: rtMs === null ? null : Math.max(0, Math.round(rtMs)),
    confusion_class: item.confusion_class ?? null,
    session_id: sessionId,
  };
}

const STIMULUS_LABELS = ["第一段", "第二段", "第三段"];

/** docs/UX.md 3 — 听辨练习 (T7). */
export function ListenPage() {
  const { assignmentId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const q = useQuery({ queryKey: qk.assignments, queryFn: () => studentApi.assignments() });
  const assignment = (q.data?.assignments ?? []).find((a) => a.id === assignmentId) ?? null;
  /**
   * 防御性过滤：服务端已经把示范录音没录全的试次剔掉了（routes/shared.ts
   * `perceptionBundle`），这里再挡一道，保证永远不会出现「没有播放键的题」。
   */
  const items = useMemo<PerceptionItem[]>(
    () => (assignment?.material.perception?.items ?? []).filter((i) => i.stimuli.length > 0),
    [assignment],
  );

  const [index, setIndex] = useState(0);
  const [trials, setTrials] = useState<TrialDraft[]>([]);
  const [finished, setFinished] = useState(false);
  const lastEndedAt = useRef<number | null>(null);

  const submit = useMutation({
    mutationFn: (rows: TrialDraft[]) =>
      studentApi.perceptionTrials(assignmentId, rows, getSessionId()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.assignments });
    },
    onError: () => toast.error("练习记录没能保存，点「再试一次」重发。"),
  });

  const item = items[index] ?? null;

  const answer = useCallback(
    (value: string) => {
      if (!item) return;
      const rt = lastEndedAt.current === null ? null : performance.now() - lastEndedAt.current;
      const row = buildTrial(item, value, rt, getSessionId());
      const next = [...trials, row];
      setTrials(next);
      lastEndedAt.current = null;
      if (index + 1 < items.length) {
        setIndex(index + 1);
      } else {
        setFinished(true);
        submit.mutate(next);
      }
    },
    [item, trials, index, items.length, submit],
  );

  if (q.isPending) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={160} />
        <Skeleton variant="rect" height={120} />
      </div>
    );
  }

  if (!assignment || items.length === 0) {
    // 有任务但一道题都放不出来 = 老师的示范录音还没录完
    const waitingForAudio = !!assignment;
    return (
      <div className="xz-container">
        <EmptyState
          icon={<Icon id="ui-listening" size={24} />}
          title={waitingForAudio ? "听辨题的示范录音还在准备中" : "这条听辨练习暂时打不开"}
          description={
            waitingForAudio
              ? "老师录好示范音之后，这里就会出现可以播放的题目。先去录别的吧。"
              : "它可能已经完成了，或者题目还在准备中。"
          }
          action={
            <Link className="gz-button gz-button--primary" to="/s">
              回到今日任务
            </Link>
          }
        />
      </div>
    );
  }

  if (finished) {
    const scored = trials.filter((t) => t.correct !== null);
    const correct = scored.filter((t) => t.correct).length;
    return (
      <div className="xz-container xz-stack">
        <Card title="练习结果" padding="lg">
          <p className="gz-type-score">
            {scored.length > 0 ? `${correct} / ${scored.length}` : `${trials.length} 题`}
          </p>
          <p className="gz-muted">
            这是一次听力练习，不是考试成绩。多听几次，对相近的音会越来越敏感。
          </p>
          {submit.isError && (
            <p className="gz-notice gz-notice--error" role="alert">
              <Icon id="ui-error" size={20} />
              练习记录还没保存成功。
            </p>
          )}
          <div className="xz-row">
            {submit.isError && (
              <Button
                variant="primary"
                loading={submit.isPending}
                onClick={() => submit.mutate(trials)}
              >
                再试一次
              </Button>
            )}
            <Button
              variant={submit.isError ? "secondary" : "primary"}
              onClick={() => void navigate("/s")}
            >
              回到今日任务
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const options = item ? answerOptions(item) : [];

  return (
    <div className="xz-container xz-stack xz-listen">
      <header className="xz-record-page__head">
        <Link className="gz-button gz-button--quiet" to="/s">
          <Icon id="ui-arrow-left" size={20} /> 任务
        </Link>
        <Tag tone="info">
          <Icon id="ui-listening" size={16} /> 听辨练习
        </Tag>
        <span className="gz-caption">
          {index + 1}/{items.length}
        </span>
      </header>

      <ProgressBar
        value={index / items.length}
        label={`第 ${index + 1} 题，共 ${items.length} 题`}
      />

      <Card padding="lg">
        {index === 0 && (
          <Illustration name="guide-headphones" size="md" caption="戴上耳机，听清楚再选" />
        )}
        <p className="xz-listen__prompt">
          {item?.prompt ??
            (item?.trial_type === "AX"
              ? "听两段录音，它们读的是同一个音吗？"
              : item?.trial_type === "ABX"
                ? "听三段录音，第三段更像前面哪一段？"
                : "听这段录音，它读的是下面哪一个？")}
        </p>

        <div className="xz-listen__stimuli">
          {(item?.stimuli ?? []).map((src, i) => (
            <div className="xz-listen__stimulus" key={`${index}-${i}`}>
              <span className="gz-eyebrow">{STIMULUS_LABELS[i] ?? `第 ${i + 1} 段`}</span>
              <AudioPlayer
                src={src}
                hideRate
                size="md"
                label={`${STIMULUS_LABELS[i] ?? `第 ${i + 1} 段`}录音`}
                onEnded={() => {
                  lastEndedAt.current = performance.now();
                }}
              />
            </div>
          ))}
        </div>

        <div className="xz-listen__answers">
          {options.map((o) => (
            <Button
              key={o.value}
              variant="secondary"
              size="lg"
              block
              onClick={() => answer(o.value)}
            >
              {o.label}
            </Button>
          ))}
        </div>
      </Card>
    </div>
  );
}
